import axios from "axios";

// Defensive: if REACT_APP_BACKEND_URL is missing or stringified-undefined at
// build time (a recurring production deploy bug), fall back to the current
// origin so the storefront keeps working as long as the backend lives on
// the same domain.
const _envBackend = process.env.REACT_APP_BACKEND_URL;
const _validEnv =
  typeof _envBackend === "string" &&
  _envBackend.trim() !== "" &&
  _envBackend.trim().toLowerCase() !== "undefined";
const _resolvedBackend = _validEnv
  ? _envBackend.replace(/\/+$/, "")
  : (typeof window !== "undefined" ? window.location.origin : "");

export const BACKEND_URL = _resolvedBackend;
export const API = `${_resolvedBackend}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  timeout: 20000,
});

// Safe localStorage access — Safari Private mode can throw on reads in edge
// cases. Silently fallback to null so the request still fires.
const safeGetItem = (key) => {
  try {
    return typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem(key)
      : null;
  } catch {
    return null;
  }
};

// Customer access-token fallback for cookie-restrictive browsers.
// Some Android in-app webviews and stricter iOS PWA contexts silently
// drop SameSite=None cookies, which leaves the cookie-only auth path
// dead. We persist the access_token in localStorage on login/refresh
// and replay it as a Bearer header — cookies stay primary; this is
// the belt-and-suspenders fallback.
const CUSTOMER_TOKEN_KEY = "hopsy_user_token";
export const setCustomerToken = (t) => {
  try { if (t) window.localStorage.setItem(CUSTOMER_TOKEN_KEY, t); } catch {}
};
export const clearCustomerToken = () => {
  try { window.localStorage.removeItem(CUSTOMER_TOKEN_KEY); } catch {}
};

api.interceptors.request.use((config) => {
  const url = typeof config.url === "string" ? config.url : "";
  // Admin token wins for /admin/* — it's the wider-permission credential.
  const adminToken = safeGetItem("hopsy_admin_token");
  if (adminToken && url.startsWith("/admin")) {
    config.headers.Authorization = `Bearer ${adminToken}`;
    return config;
  }
  // For all other routes, attach the customer access token if we have one.
  // The server still trusts cookies first; this is purely a fallback.
  const userToken = safeGetItem(CUSTOMER_TOKEN_KEY);
  if (userToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${userToken}`;
  }
  return config;
});

// Silent refresh on 401 — customer access_token cookie has a 24h lifetime;
// if it expires while the user is editing, auto-refresh once using the
// 30-day refresh cookie and re-play the original request.
let isRefreshing = false;
let pendingQueue = [];
const queueSubscriber = (cb) => { pendingQueue.push(cb); };
const flushQueue = (err) => { pendingQueue.forEach((cb) => cb(err)); pendingQueue = []; };

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error?.response?.status;
    const url = original?.url || "";
    const isAuthRoute = url.includes("/auth/");
    if (status === 401 && !original._retry && !isAuthRoute) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queueSubscriber((err) => {
            if (err) return reject(err);
            original._retry = true;
            resolve(api(original));
          });
        });
      }
      original._retry = true;
      isRefreshing = true;
      try {
        const { data: refreshData } = await api.post("/auth/refresh");
        // Rotate the localStorage fallback token so the next call carries
        // the freshly-minted access token even if cookies are dropped.
        if (refreshData?.access_token) setCustomerToken(refreshData.access_token);
        flushQueue(null);
        return api(original);
      } catch (refreshErr) {
        flushQueue(refreshErr);
        // Refresh decisively failed — drop the stale fallback token so
        // the next login replaces it cleanly.
        clearCustomerToken();
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export const WHATSAPP_NUMBER = "917822086946";
export const BRAND_EMAIL = "hey@hayhopsy.com";
export const BRAND_PHONE = "+91-7822086946";
export const INSTAGRAM = "https://instagram.com/hayhopsy";
export const FACEBOOK = "https://facebook.com/hayhopsy";

export const CATEGORIES = [
  { id: "all", label: "All Products" },
  { id: "hay", label: "Hay" },
  { id: "pellets", label: "Pellets" },
  { id: "specialty", label: "Specialty" },
  { id: "bedding", label: "Bedding" },
  { id: "toys", label: "Toys" },
];

export const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

export const whatsappLink = (text) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
